import { useManager } from 'dreamstate';
import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Flex, Box, rem } from '@vimeo/bokeh';

import { QnAManager, useQnaQuestions } from '../../../store/firebase/interaction/qna';
import { T_ACTIVE, T_APPROVED, T_ARCHIVED, T_REVIEW } from '../../../translations';
import { EQnaTab, IQnAPanel, IQnAQuestion } from '../../../types/interaction_tools';
import { inline } from '../../../utils/general/lambda';

export function useQnaPanels() {
  const [activePanelId, setActivePanelId] = useState<EQnaTab>(EQnaTab.PENDING);
  const {
    activeSessionPendingQuestions,
    activeSessionQuestions,
    isEventModerated,
    config: { canUseQnaModeration, canViewQnaModeration },
  } = useManager(QnAManager);

  const isWithModeratedQuestions: boolean = Boolean(
    isEventModerated && (canUseQnaModeration || canViewQnaModeration),
  );

  useEffect(() => {
    setActivePanelId(isWithModeratedQuestions ? EQnaTab.PENDING : EQnaTab.ACTIVE);
  }, [isWithModeratedQuestions]);

  const { activeQuestions, pendingQuestions, archivedQuestions } = useQnaQuestions(
    activeSessionPendingQuestions,
    activeSessionQuestions,
  );

  const panels: Array<IQnAPanel> = useMemo(() => {
    const pendingQuestionsLabel: ReactNode = (
      <Flex gap={rem(4)}>
        <Box>{T_REVIEW}</Box>
        <Box color={'text-secondary'}>{pendingQuestions.length || null}</Box>
      </Flex>
    );
    const approvedQuestionsLabel: ReactNode = (
      <Flex gap={rem(4)}>
        <Box>{T_APPROVED}</Box>
        <Box color={'text-secondary'}>{activeQuestions.length || null}</Box>
      </Flex>
    );
    const activeQuestionsLabel: ReactNode = (
      <Flex gap={rem(4)}>
        <Box>{T_ACTIVE}</Box>
        <Box color={'text-secondary'}>{activeQuestions.length || null}</Box>
      </Flex>
    );
    const archivedQuestionsLabel: ReactNode = (
      <Flex gap={rem(4)}>
        <Box>{T_ARCHIVED}</Box>
        <Box color={'text-secondary'}>{archivedQuestions.length || null}</Box>
      </Flex>
    );

    return isWithModeratedQuestions
      ? [
          {
            id: EQnaTab.PENDING,
            label: pendingQuestionsLabel,
            questions: pendingQuestions,
          },
          {
            id: EQnaTab.APPROVED,
            label: approvedQuestionsLabel,
            questions: activeQuestions,
          },
          {
            id: EQnaTab.ARCHIVED,
            label: archivedQuestionsLabel,
            questions: archivedQuestions,
          },
        ]
      : [
          {
            id: EQnaTab.ACTIVE,
            label: activeQuestionsLabel,
            questions: activeQuestions,
          },
          {
            id: EQnaTab.ARCHIVED,
            label: archivedQuestionsLabel,
            questions: archivedQuestions,
          },
        ];
  }, [pendingQuestions, activeQuestions, archivedQuestions, isWithModeratedQuestions]);

  const currentQuestions: Array<IQnAQuestion> = inline(() => {
    switch (activePanelId) {
      case EQnaTab.PENDING:
        return pendingQuestions;

      case EQnaTab.ARCHIVED:
        return archivedQuestions;

      default:
        return activeQuestions;
    }
  });

  return {
    panels,
    activePanelId,
    currentQuestions,
    activeQuestions,
    pendingQuestions,
    archivedQuestions,
    setActivePanelId,
  };
}
